import { useNavigate } from "react-router-dom";
import { proxiedImageUrl } from "../lib/imageProxy";
import { Button } from "../components/ui/button";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { PromoVideo } from "../components/PromoVideo";
import axios from "axios";
import {
  Zap, CheckCircle2, ArrowRight, Mail,
  Star, Wrench, Briefcase, ChefHat, Camera, Users, X, Check,
  Clock, FileText, Settings, Server, Sparkles, Lock, Shield, Flag,
  Scissors, Heart, GraduationCap, Truck, Paintbrush, Plug, Home,
  Menu, ChevronDown, ChevronUp, Globe, Cpu, BarChart3, Palette, Crown, Rocket
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  { icon: Wrench, label: "Handwerker" },
  { icon: Users, label: "Coaches & Berater" },
  { icon: ChefHat, label: "Restaurants & Cafes" },
  { icon: Camera, label: "Freelancer" },
  { icon: Briefcase, label: "Lokale Dienstleister" },
  { icon: Scissors, label: "Friseure" },
  { icon: Heart, label: "Physiotherapie" },
  { icon: GraduationCap, label: "Nachhilfe" },
  { icon: Truck, label: "Umzugsunternehmen" },
  { icon: Paintbrush, label: "Maler & Lackierer" },
  { icon: Plug, label: "Elektriker" },
  { icon: Home, label: "Hausmeisterservice" },
];

const COMPARE_ROWS = [
  { them: "Stundenlange Einrichtung", us: "Webseite in wenigen Minuten live" },
  { them: "Texte selbst schreiben", us: "KI generiert Struktur & Inhalte" },
  { them: "Manuelles Design", us: "Automatisches Design-System" },
  { them: "Hosting separat buchen", us: "Hosting & Infrastruktur integriert" },
  { them: "Hohe Agenturkosten", us: "Skalierbar ab 29€/Monat", highlight: true },
];

const ROW_TOP = [
  { icon: Wrench, label: "Handwerker" },
  { icon: Users, label: "Coaches & Berater" },
  { icon: ChefHat, label: "Restaurants & Cafes" },
  { icon: Camera, label: "Freelancer" },
  { icon: Briefcase, label: "Lokale Dienstleister" },
  { icon: Scissors, label: "Friseure" },
];

const ROW_BOTTOM = [
  { icon: Heart, label: "Physiotherapie" },
  { icon: GraduationCap, label: "Nachhilfe" },
  { icon: Truck, label: "Umzugsunternehmen" },
  { icon: Paintbrush, label: "Maler & Lackierer" },
  { icon: Plug, label: "Elektriker" },
  { icon: Home, label: "Hausmeisterservice" },
];

const FAQ_ITEMS = [
  { q: "Wie schnell ist meine Webseite online?", a: "Nach erfolgreicher Bestellung wird deine Webseite automatisch veröffentlicht. In der Regel ist sie innerhalb weniger Minuten live – inklusive SSL-Verschlüsselung." },
  { q: "Was sind Content-Updates?", a: "Content-Updates sind kleine Änderungen an deiner bestehenden Webseite, zum Beispiel: Texte anpassen, Bilder austauschen, Öffnungszeiten ändern oder Leistungen ergänzen. Je nach Paket sind 3, 5 oder 10 Updates pro Monat enthalten. Du kannst sie jederzeit über dein Dashboard einreichen." },
  { q: "Kann ich mein Paket später ändern?", a: "Ja. Du kannst jederzeit upgraden oder downgraden. Beim Upgrade stehen dir zusätzliche Funktionen sofort zur Verfügung." },
  { q: "Sind meine Daten und meine Webseite sicher?", a: "Ja. Alle Webseiten sind SSL-verschlüsselt. Je nach Paket sind automatische Backups inklusive." },
  { q: "Wie funktioniert die Zahlung?", a: "Die Zahlung erfolgt sicher über Stripe. Alle Zahlungen sind verschlüsselt und entsprechen aktuellen Sicherheitsstandards." },
  { q: "Kann ich jederzeit kündigen?", a: "Ja. Alle Pakete sind monatlich kündbar – ohne Mindestlaufzeit." },
  { q: "Brauche ich technische Kenntnisse?", a: "Nein. Wir kümmern uns um Hosting, Wartung, Sicherheit und Updates. Du kannst dich vollständig auf dein Geschäft konzentrieren." },
];

const FEATURES = [
  { icon: Sparkles, title: "KI-generierte Texte", desc: "Professionelle, branchenspezifische Texte die konvertieren – automatisch erstellt von GPT.", color: "from-indigo-500 to-blue-500" },
  { icon: Globe, title: "Eigene Domain & SSL", desc: "Subdomain sofort verfügbar oder eigene .de/.com Domain mit automatischem SSL-Zertifikat.", color: "from-emerald-500 to-teal-500" },
  { icon: Cpu, title: "SEO-Optimierung", desc: "Meta-Tags, Schema-Markup und schnelle Ladezeiten – alles automatisch für Google optimiert.", color: "from-amber-500 to-orange-500" },
  { icon: BarChart3, title: "Leads & Anfragen", desc: "Integriertes Kontaktformular mit Dashboard. Sieh alle Anfragen in Echtzeit.", color: "from-pink-500 to-rose-500" },
  { icon: Palette, title: "3 Design-Stile", desc: "Modern, Seriös oder Freundlich – wähle den Stil der zu deiner Branche passt.", color: "from-violet-500 to-purple-500" },
  { icon: Shield, title: "DSGVO & Impressum", desc: "Automatisch generiertes Impressum und Datenschutzerklärung. Rechtssicher ab Tag 1.", color: "from-cyan-500 to-blue-500" },
];

function StickyHeader({ navigate, isAuthenticated }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navItems = [
    { label: "Webseiten-Check", action: () => navigate("/webseiten-check") },
    { label: "Funktionen", id: "funktionen" },
    { label: "Preise", id: "preise" },
    { label: "FAQ", id: "faq" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-2 sm:py-3"
          : "py-4 sm:py-6"
      }`}
      data-testid="landing-header"
    >
      <div className={`mx-3 sm:mx-6 lg:mx-auto max-w-6xl transition-all duration-500 rounded-2xl ${
        scrolled || mobileOpen
          ? "bg-white/70 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-white/50 ring-1 ring-slate-900/[0.04]"
          : "bg-transparent"
      }`}>
        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2" data-testid="header-logo">
            <Logo size="lg" clickable={false} />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.label}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={item.action || (() => scrollTo(item.id))}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100/60 transition-all"
              >
                {item.label}
              </button>
            ))}
            <div className="w-px h-6 bg-slate-200 mx-2" />
            {isAuthenticated ? (
              <Button
                data-testid="header-dashboard"
                onClick={() => navigate("/meine-seiten")}
                variant="outline"
                className="h-9 px-5 rounded-full text-sm font-semibold border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-all"
              >
                Mein Dashboard
              </Button>
            ) : (
              <button
                data-testid="header-login"
                onClick={() => navigate("/anmelden?mode=login")}
                className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 rounded-lg hover:bg-indigo-50/60 transition-all"
              >
                Anmelden
              </button>
            )}
            <div className="relative group/btn inline-block">
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500 opacity-50 blur-md group-hover/btn:opacity-80 transition-opacity" />
              <Button
                data-testid="header-cta"
                onClick={() => navigate("/assistent")}
                className="relative h-9 px-5 rounded-full text-sm font-semibold bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 text-white transition-all"
              >
                Jetzt starten
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-slate-100/60 transition-colors"
          >
            {mobileOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            mobileOpen ? "max-h-72 opacity-100" : "max-h-0 opacity-0"
          }`}
          data-testid="mobile-menu"
        >
          <div className="px-4 pb-4 pt-1 border-t border-slate-100/60">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  onClick={item.action || (() => scrollTo(item.id))}
                  className="px-4 py-3 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded-xl text-left transition-colors"
                >
                  {item.label}
                </button>
              ))}
              {isAuthenticated ? (
                <Button
                  data-testid="mobile-dashboard"
                  onClick={() => { setMobileOpen(false); navigate("/meine-seiten"); }}
                  variant="outline"
                  className="mt-2 h-11 rounded-xl text-sm font-semibold border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                >
                  Mein Dashboard
                </Button>
              ) : (
                <button
                  data-testid="mobile-login"
                  onClick={() => { setMobileOpen(false); navigate("/anmelden?mode=login"); }}
                  className="mt-2 h-11 rounded-xl text-sm font-medium text-indigo-600 hover:bg-indigo-50 border border-indigo-200 w-full"
                >
                  Anmelden
                </button>
              )}
              <Button
                data-testid="mobile-cta"
                onClick={() => { setMobileOpen(false); navigate("/assistent"); }}
                className="mt-2 h-11 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 text-white shadow-lg shadow-indigo-500/25"
              >
                Jetzt starten
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="px-4 md:px-8 py-20 md:py-28 scroll-mt-24" data-testid="faq-section">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">FAQ</span>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Häufige Fragen</h2>
        </div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                data-testid={`faq-item-${i}`}
                className={`rounded-2xl border transition-all duration-300 ${
                  isOpen ? "bg-white border-indigo-200 shadow-lg shadow-indigo-500/5" : "bg-white/60 border-slate-200 hover:border-slate-300"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-slate-900 text-sm sm:text-base pr-4">{item.q}</span>
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isOpen ? "bg-indigo-100" : "bg-slate-100"}`}>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-indigo-600" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
                  <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection({ navigate }) {
  return (
    <section id="funktionen" className="px-4 md:px-8 py-24 md:py-32 scroll-mt-24 relative overflow-hidden" data-testid="features-section">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-white to-slate-50/40 pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Headline */}
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-[11px] font-bold text-indigo-500 uppercase tracking-[0.2em] mb-4">Automatisiertes Webseiten-System</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-5 tracking-tight leading-[1.15]">
            Webseiten, die sich selbst<br className="hidden sm:block" /> veröffentlichen.
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            Unsere KI übernimmt Generierung, Infrastruktur und Live-Schaltung.<br className="hidden sm:block" />
            Du bestätigst nur noch – wir erledigen den Rest.
          </p>
        </div>

        {/* 3 Process Modules */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-0 relative">
          {/* Connection lines (desktop only) */}
          <div className="hidden lg:block absolute top-24 left-[33.33%] right-[33.33%] h-px">
            <div className="h-full bg-gradient-to-r from-indigo-200 via-blue-200 to-emerald-200" />
          </div>

          {/* Module 1 */}
          <div data-testid="process-step-1" className="group relative">
            <div className="relative bg-white rounded-2xl p-7 sm:p-8 border border-slate-100 hover:border-indigo-200/60 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_60px_-15px_rgba(99,102,241,0.15)] h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-lg shadow-indigo-500/20 group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em]">Schritt 1</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug">
                KI-Engine erstellt deine komplette Webseite
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5">
                Branchenspezifische Texte, Seitenstruktur und SEO-Optimierung werden automatisch generiert. Kein Editor. Kein Copywriting. Keine manuelle Konfiguration.
              </p>
              <ul className="space-y-2.5">
                {["KI-Texte mit Conversion-Fokus", "Automatische SEO-Struktur", "Branchenoptimiertes Layout"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Module 2 */}
          <div data-testid="process-step-2" className="group relative">
            <div className="relative bg-white rounded-2xl p-7 sm:p-8 border border-slate-100 hover:border-blue-200/60 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.15)] h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em]">Schritt 2</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug">
                Infrastruktur wird automatisch bereitgestellt
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5">
                Domain, Hosting, DNS und SSL werden im Hintergrund eingerichtet. Du musst nichts technisch konfigurieren.
              </p>
              <ul className="space-y-2.5">
                {["Wunschdomain oder Subdomain", "SSL-Zertifikat automatisch aktiviert", "Performance-optimiertes Hosting"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Module 3 */}
          <div data-testid="process-step-3" className="group relative">
            <div className="relative bg-white rounded-2xl p-7 sm:p-8 border border-slate-100 hover:border-emerald-200/60 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_60px_-15px_rgba(16,185,129,0.15)] h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20 group-hover:scale-110 transition-transform duration-300">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em]">Schritt 3</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug">
                Ein Klick – deine Webseite ist live
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-5">
                Mit einem Klick veröffentlichst du deine Seite. Rechtssicher, mobil optimiert und sofort erreichbar.
              </p>
              <ul className="space-y-2.5">
                {["DSGVO & Impressum automatisch generiert", "Lead-Formular inklusive Dashboard", "Sofort online erreichbar"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Mini CTA */}
        <div className="text-center mt-16 md:mt-20">
          <p className="text-slate-500 mb-5 text-base">Bereit, deine Webseite automatisch live zu schalten?</p>
          <button
            onClick={() => navigate("/assistent")}
            data-testid="features-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 text-white font-semibold text-sm shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-300 hover:-translate-y-0.5"
          >
            Jetzt starten <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

const heroProxyImg = (() => proxiedImageUrl("https://images.unsplash.com/photo-1659353589241-f450b33f908a?w=600&q=60"))();

function ExampleSection() {
  return (
    <section className="py-24 bg-slate-50 border-y border-slate-200 overflow-hidden" data-testid="example-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">Vorher / Nachher</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Der Unterschied ist sichtbar.
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto text-base leading-relaxed">
            Verabschiede dich von mittelmäßigen Baukasten-Lösungen. Unsere KI erstellt modernes, verkaufsstarkes Design, das Kunden überzeugt.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* --- LEFT: TRADITIONAL / BAUKASTEN --- */}
          <div className="relative group mx-auto max-w-md lg:max-w-full w-full">
            <div className="text-center mb-4">
              <span className="text-sm font-bold text-red-500 uppercase tracking-wider bg-red-50 px-3 py-1 rounded-full">
                Traditioneller Baukasten
              </span>
            </div>
            
            <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-[500px]">
              {/* Browser Bar */}
              <div className="bg-slate-100 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                </div>
                <div className="flex-1 bg-white border border-slate-200 rounded text-[10px] text-slate-400 px-2 py-0.5 text-center mx-2 truncate">
                  www.maler-mueller-1985-baukasten.de
                </div>
              </div>

              {/* Scrolling Content Container */}
              <div className="h-full overflow-hidden relative">
                {/* Animation Wrapper */}
                <div className="absolute top-0 left-0 right-0 animate-scroll-slow">
                  {/* --- WEBSITE CONTENT (Repeated for loop) --- */}
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white pb-8">
                      {/* Header */}
                      <header className="border-b border-gray-300 py-4 px-4 bg-white flex flex-col items-center gap-2">
                        <h1 className="text-xl font-serif font-bold text-gray-800">Malerbetrieb Müller</h1>
                        <nav className="text-[10px] text-gray-600 space-x-2 font-sans">
                          <span className="underline">Startseite</span>
                          <span>Über uns</span>
                          <span>Leistungen</span>
                          <span>Kontakt</span>
                        </nav>
                      </header>

                      {/* Hero Area */}
                      <div className="relative h-64 bg-gray-200">
                        <img 
                          src={heroProxyImg} 
                          alt="Generic construction" 
                          className="w-full h-full object-cover opacity-80"
                        />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/90 p-4 text-center w-3/4 shadow-sm border border-gray-300">
                          <h2 className="text-lg font-serif font-bold text-gray-800 mb-1">Willkommen bei uns</h2>
                          <p className="text-xs text-gray-600 mb-2">Ihr Partner für Farbe und mehr seit 1995.</p>
                          <button className="bg-gray-700 text-white text-xs px-3 py-1.5 rounded-sm">Mehr erfahren</button>
                        </div>
                      </div>

                      {/* Text Wall */}
                      <div className="p-6">
                        <h3 className="text-base font-bold text-gray-800 mb-2 font-serif">Wir machen Ihre Wände schön</h3>
                        <p className="text-xs text-gray-600 leading-relaxed text-justify">
                          Herzlich Willkommen auf der Webseite von Malerbetrieb Müller. Wir sind ein Traditionsunternehmen und bieten Ihnen alle Leistungen rund um das Malerhandwerk. Ob Innenanstrich, Fassade oder Lackierarbeiten - bei uns sind Sie an der richtigen Adresse. Qualität steht bei uns an erster Stelle. Rufen Sie uns gerne an für ein unverbindliches Angebot. Wir freuen uns auf Sie.
                        </p>
                        <br />
                        <p className="text-xs text-gray-600 leading-relaxed text-justify">
                          Unsere Mitarbeiter sind stehts bemüht ihre Wünsche zu erfüllen. Farbe ist unser Leben. Lassen Sie sich beraten.
                        </p>
                      </div>

                      {/* Generic Services */}
                      <div className="bg-gray-100 p-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-3 border border-gray-200 text-center">
                            <div className="w-8 h-8 bg-gray-300 mx-auto mb-2"></div>
                            <span className="text-xs font-bold text-gray-700">Anstrich</span>
                          </div>
                          <div className="bg-white p-3 border border-gray-200 text-center">
                            <div className="w-8 h-8 bg-gray-300 mx-auto mb-2"></div>
                            <span className="text-xs font-bold text-gray-700">Fassade</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* --- RIGHT: WEBGENERATOR (MODERN) --- */}
          <div className="relative group mx-auto max-w-md lg:max-w-full w-full">
            <div className="text-center mb-4">
              <span className="text-sm font-bold text-indigo-600 uppercase tracking-wider bg-indigo-50 px-3 py-1 rounded-full flex items-center justify-center gap-2 w-fit mx-auto shadow-sm border border-indigo-100">
                <Sparkles className="w-3.5 h-3.5" />
                AI-Powered WebGenerator
              </span>
            </div>

            <div className="relative bg-white rounded-xl border-4 border-indigo-50 shadow-2xl shadow-indigo-500/10 overflow-hidden h-[500px]">
              {/* Browser Bar */}
              <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10 relative shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                </div>
                <div className="bg-slate-50 text-[10px] text-slate-500 px-3 py-1 rounded-full flex items-center gap-1.5">
                  <Lock className="w-2.5 h-2.5 text-green-500" />
                  maler-mueller-muenchen.de
                </div>
                <div className="w-10"></div>
              </div>

              {/* Scrolling Content Container */}
              <div className="h-full overflow-hidden relative bg-white">
                {/* Animation Wrapper */}
                <div className="absolute top-0 left-0 right-0 animate-scroll-normal">
                  {/* --- WEBSITE CONTENT (Repeated for loop) --- */}
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white">
                      {/* Modern Header */}
                      <header className="px-6 py-4 flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur-sm z-10">
                        <span className="font-bold text-lg text-slate-900 tracking-tight">MÜLLER.</span>
                        <div className="flex items-center gap-4">
                          <nav className="hidden sm:flex gap-4 text-xs font-medium text-slate-600">
                            <span>Leistungen</span>
                            <span>Projekte</span>
                          </nav>
                          <button className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-indigo-700 transition-colors">
                            Kontakt
                          </button>
                        </div>
                      </header>

                      {/* Modern Hero */}
                      <div className="relative h-80 px-6 py-12 flex items-center">
                        <img 
                          src="https://images.pexels.com/photos/5493655/pexels-photo-5493655.jpeg?auto=compress&cs=tinysrgb&w=800" 
                          alt="Professional Painter" 
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent"></div>
                        <div className="relative z-10 max-w-sm text-white">
                          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white/10 backdrop-blur rounded-full border border-white/20 text-[10px] font-medium mb-3">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                            Meisterbetrieb in München
                          </div>
                          <h2 className="text-3xl font-extrabold leading-tight mb-3">
                            Premium Malerarbeiten.<br/>Ohne Stress.
                          </h2>
                          <p className="text-sm text-slate-300 mb-6 leading-relaxed">
                            Wir verwandeln Ihre Räume. Pünktlich, sauber und zum Festpreis. Erhalten Sie Ihr Angebot in unter 24 Stunden.
                          </p>
                          <div className="flex gap-3">
                            <button className="bg-white text-slate-900 text-xs font-bold px-5 py-2.5 rounded-full hover:bg-slate-50 transition-colors">
                              Angebot anfordern
                            </button>
                            <button className="text-white text-xs font-medium px-4 py-2.5 border border-white/30 rounded-full hover:bg-white/10 transition-colors">
                              Unsere Arbeiten
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Trust Bar */}
                      <div className="bg-slate-50 py-4 px-6 flex justify-around border-b border-slate-100">
                        {['5 Sterne Google', 'TÜV Zertifiziert', 'Festpreis'].map((t, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-default">
                            <Star className="w-3 h-3 fill-slate-400 text-slate-400" />
                            <span className="text-[10px] font-bold text-slate-600">{t}</span>
                          </div>
                        ))}
                      </div>

                      {/* Services Teaser */}
                      <div className="py-10 px-6">
                        <div className="text-center mb-6">
                          <h3 className="text-lg font-bold text-slate-900">Unsere Expertise</h3>
                          <p className="text-xs text-slate-500 mt-1">Qualität bis ins Detail.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { title: "Innenanstrich", icon: Paintbrush, color: "text-blue-500 bg-blue-50" },
                            { title: "Fassaden", icon: Home, color: "text-orange-500 bg-orange-50" }
                          ].map((s, idx) => (
                            <div key={idx} className="p-4 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow bg-white">
                              <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
                                <s.icon className="w-4 h-4" />
                              </div>
                              <h4 className="font-bold text-sm text-slate-900">{s.title}</h4>
                              <p className="text-[10px] text-slate-500 mt-1 leading-snug">Perfekte Ergebnisse für Ihr Zuhause.</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Spacer to separate loops */}
                      <div className="h-12 bg-white"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
        
        <style>{`
          @keyframes scroll {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          .animate-scroll-slow {
            animation: scroll 25s linear infinite;
          }
          .animate-scroll-normal {
            animation: scroll 20s linear infinite;
          }
        `}</style>
      </div>
    </section>
  );
}


const TIER_ICONS = { basis: Zap, pro: Shield, premium: Crown };

function PricingSection({ navigate }) {
  const FALLBACK_PACKAGES = [
    { id: "basis", name: "Starter", price: 29, currency: "EUR", interval: "Monat", popular: false, headline: "Online sichtbar werden", features: ["Professionelle Landingpage inkl. Hosting","SSL-Zertifikat & DSGVO-konform","Wir halten deine Seite aktuell (3x/Monat)","Perfekt auf Handy, Tablet & Desktop","Gefunden werden: Local-SEO Grundpaket","Pers\u00f6nlicher E-Mail-Support"], cta: "Starter w\u00e4hlen", description: "Dein professioneller Online-Auftritt, ohne Technik-Stress." },
    { id: "pro", name: "Pro", price: 49, currency: "EUR", interval: "Monat", popular: true, headline: "Mehr Kunden gewinnen", features: ["Alles aus Starter, plus:","Deine Seite bleibt immer aktuell (5x/Monat)","Automatische Sicherung jede Woche","Mehr Anfragen durch A/B-Tests & Mini-CRM","Top-Platzierung: Google Maps + Keywords","Schnelle Hilfe per Chat-Support","Telefonisches Onboarding mit persönlichem Rückruf"], cta: "Pro w\u00e4hlen", description: "Damit deine Seite nicht nur gut aussieht, sondern auch Kunden bringt." },
    { id: "premium", name: "Premium", price: 79, currency: "EUR", interval: "Monat", popular: false, headline: "Wir \u00fcbernehmen dein Marketing", features: ["Alles aus Pro, plus:","10 \u00c4nderungen pro Monat \u2013 deine Seite, immer frisch","Automatische Follow-Up E-Mails an Interessenten","Werbekampagnen: 3\u20135 Anzeigen inkl. Visuals","T\u00e4gliche Backups & sofortige Wiederherstellung","Individuelle Anpassungen nach deinen W\u00fcnschen","Pers\u00f6nlicher Ansprechpartner & 1-on-1 Beratung","Telefonisches Onboarding mit persönlichem Rückruf"], cta: "Premium w\u00e4hlen", description: "Volle Marketing-Power & t\u00e4gliche Backups. Wir k\u00fcmmern uns um dein Online-Wachstum." },
  ];
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [subtitle, setSubtitle] = useState("Alle Pakete monatlich kündbar – inklusive Hosting & Wartung.");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        if (res.data.packages?.length) setPackages(res.data.packages);
        if (res.data.subtitle) setSubtitle(res.data.subtitle);
      } catch (e) { /* use fallback */ }
    })();
  }, []);

  if (!packages.length) return null;

  return (
    <section id="preise" className="px-4 md:px-8 py-20 md:py-28 scroll-mt-24" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">Pricing</span>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Wähle dein Paket</h2>
          <p className="text-slate-500 max-w-lg mx-auto text-sm sm:text-base">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 items-start">
          {packages.map((pkg) => {
            const isPro = pkg.id === "pro";
            const isPremium = pkg.id === "premium";
            const Icon = TIER_ICONS[pkg.id] || Zap;

            return (
              <div key={pkg.id} data-testid={`landing-price-${pkg.id}`} className={`relative ${isPro ? "md:-mt-4" : ""}`}>
                {/* Glow for Pro */}
                {isPro && (
                  <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-violet-500 opacity-80 blur-[2px]" />
                )}
                {/* Subtle glow for Premium */}
                {isPremium && (
                  <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-amber-500/40 via-amber-600/30 to-yellow-500/40 blur-[3px]" />
                )}

                <div className={`relative rounded-2xl flex flex-col overflow-hidden ${
                  isPremium ? "bg-gradient-to-b from-slate-900 to-slate-800" :
                  isPro ? "bg-white shadow-2xl shadow-indigo-500/20" :
                  "bg-white border-2 border-slate-200"
                }`}>
                  {/* Badge */}
                  {isPro && (
                    <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 text-center py-2.5 text-sm font-semibold text-white">
                      <div className="flex items-center justify-center gap-1.5">
                        <Zap className="w-3.5 h-3.5" />
                        AI-Powered Favorit
                      </div>
                    </div>
                  )}
                  {isPremium && (
                    <div className="bg-amber-400/20 text-center py-2.5 text-sm font-semibold text-amber-300">
                      <div className="flex items-center justify-center gap-1.5">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        Sorglos-Paket
                      </div>
                    </div>
                  )}

                  <div className={`p-7 flex flex-col flex-1 ${!isPro && !isPremium ? "pt-8" : ""}`}>
                    <div className="mb-5">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
                        isPremium ? "bg-amber-400/20 text-amber-400" :
                        isPro ? "bg-indigo-100 text-indigo-600" :
                        "bg-indigo-50 text-indigo-600"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className={`text-xl font-bold mb-1 ${isPremium ? "text-white" : "text-slate-900"}`}>{pkg.name}</h3>
                      <p className={`text-sm font-medium ${isPremium ? "text-amber-400" : "text-indigo-600"}`}>{pkg.headline}</p>
                    </div>

                    <div className="mb-2 flex items-baseline gap-1">
                      <span className={`text-5xl font-bold tracking-tight ${isPremium ? "text-white" : "text-slate-900"}`}>{pkg.price.toFixed(0)}</span>
                      <span className="text-lg text-slate-400">€</span>
                      <span className="text-sm text-slate-400">/{pkg.interval}</span>
                    </div>

                    <p className={`text-sm mb-6 leading-relaxed ${isPremium ? "text-slate-300" : "text-slate-500"}`}>{pkg.description}</p>

                    {/* CTA */}
                    {isPro ? (
                      <div className="relative group/btn mb-7">
                        <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500 opacity-50 blur-md group-hover/btn:opacity-80 transition-opacity" />
                        <Button
                          data-testid={`landing-cta-${pkg.id}`}
                          onClick={() => navigate("/assistent")}
                          className="relative w-full h-12 rounded-xl font-semibold text-base bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500"
                        >
                          {pkg.cta}<ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        data-testid={`landing-cta-${pkg.id}`}
                        onClick={() => navigate("/assistent")}
                        className={`w-full h-12 rounded-xl font-semibold text-base mb-7 ${
                          isPremium ? "bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/25" :
                          "bg-slate-900 hover:bg-slate-800"
                        }`}
                      >
                        {pkg.cta}<ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}

                    <ul className="space-y-3 flex-1">
                      {pkg.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isPremium ? "bg-amber-400/20" : "bg-indigo-50"}`}>
                            <Check className={`w-3 h-3 ${isPremium ? "text-amber-400" : "text-indigo-500"}`} />
                          </div>
                          <span className={`text-sm ${isPremium ? "text-slate-300" : "text-slate-600"}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AudienceMarquee() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const renderChip = (a, i) => {
    const Icon = a.icon;
    return (
      <div key={i} className="marquee-chip" data-testid={`audience-chip-${a.label.toLowerCase().replace(/\s+/g, '-')}`}>
        <div className="chip-icon-wrap">
          <Icon className="w-[18px] h-[18px]" />
        </div>
        <span>{a.label}</span>
      </div>
    );
  };

  return (
    <div ref={sectionRef}>
      <p className={`text-sm font-semibold text-indigo-600 uppercase tracking-widest mb-8 text-center transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        Perfekt für
      </p>

      <div className={`flex flex-col gap-4 transition-all duration-700 ease-out delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        {/* Row 1 → scrolls left */}
        <div className="marquee-row">
          <div className="marquee-track-left">
            {[...ROW_TOP, ...ROW_TOP, ...ROW_TOP].map(renderChip)}
          </div>
        </div>

        {/* Row 2 → scrolls right */}
        <div className="marquee-row">
          <div className="marquee-track-right">
            {[...ROW_BOTTOM, ...ROW_BOTTOM, ...ROW_BOTTOM].map(renderChip)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen hero-gradient">
      {/* Sticky Header */}
      <StickyHeader navigate={navigate} isAuthenticated={isAuthenticated} />

      <main>
        {/* ===== HERO ===== */}
        <section className="px-4 md:px-8 pt-28 sm:pt-36 md:pt-40 pb-16 md:pb-20">
          <div className="max-w-4xl mx-auto text-center stagger-children">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 text-sm font-medium mb-8" data-testid="hero-badge">
              <Zap className="w-4 h-4" />
              <span>Kostenlos starten – keine Anmeldung nötig</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6" data-testid="hero-headline">
              Deine Webseite.
              <span className="block gradient-text">Vollautomatisch online.</span>
            </h1>

            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-4 leading-relaxed" data-testid="hero-subheadline">
              Gewinne neue Kunden mit einer professionellen Webseite durch KI-Logik - inklusive Veröffentlichung auf deiner Wunschdomain.
            </p>

            <p className="text-sm text-slate-500 max-w-lg mx-auto mb-10 leading-relaxed">
              Kein Baukasten. Kein technischer Aufwand. Kein Agenturprozess.
            </p>

            <div className="relative group/btn inline-block">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500 opacity-50 blur-lg group-hover/btn:opacity-80 transition-opacity" />
              <Button
                data-testid="cta-generate-button"
                onClick={() => navigate("/assistent")}
                className="relative h-14 px-10 rounded-full text-lg font-medium bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 hover:from-indigo-500 hover:via-blue-500 hover:to-violet-500 shadow-lg hover:-translate-y-0.5 transition-all"
              >
                Jetzt kostenlos starten
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-green-500" />SSL-gesichert</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-green-500" />DSGVO-konform</span>
              <span className="flex items-center gap-1.5"><Flag className="w-4 h-4 text-green-500" />EU-Hosting</span>
            </div>

            {/* Social Proof */}
            <div className="mt-10 max-w-md mx-auto" data-testid="hero-social-proof">
              <div className="flex justify-center gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote className="text-slate-600 italic text-sm leading-relaxed">
                "Von der Generierung bis zur Live-Schaltung – alles automatisiert &amp; in wenigen Minuten Online."
              </blockquote>
              <p className="text-xs text-slate-400 mt-2">— Sarah M., Friseursalon</p>
            </div>
          </div>
        </section>

        <PromoVideo />

        {/* ===== FÜR WEN? – Infinite Loop Slider ===== */}
        <section className="py-16 bg-white border-y border-slate-100 overflow-hidden" data-testid="audience-section">
          <AudienceMarquee />
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="px-4 md:px-8 py-20" data-testid="how-it-works-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-12">
              So einfach geht's
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Daten eingeben", desc: "Firmenname, Branche und Stadt – mehr brauchst du nicht" },
                { step: "2", title: "KI generiert", desc: "Professionelle Texte, Bilder und Design in Sekunden" },
                { step: "3", title: "Veröffentlichen", desc: "Mit einem Klick online – fertig!" }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== VERGLEICH ===== */}
        <section className="px-4 md:px-8 py-20 md:py-28 relative overflow-hidden" data-testid="compare-section">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/[0.04] rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-4xl mx-auto relative">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">Next Gen</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Die nächste Generation von Business-Webseiten.
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-sm sm:text-base">
                Automatisch erstellt durch KI – ohne Agentur, ohne Baukasten, ohne Setup.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              {/* Left: Traditional */}
              <div className="rounded-2xl border border-slate-200/80 bg-white/60 backdrop-blur-sm p-6 sm:p-8">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-6">Traditionelle Lösungen</p>
                <div className="space-y-5">
                  {COMPARE_ROWS.map((row, i) => (
                    <div key={i} className="flex items-start gap-3.5">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <X className="w-3 h-3 text-slate-400" />
                      </div>
                      <span className="text-sm text-slate-500 leading-relaxed">{row.them}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: AI-Powered – elevated */}
              <div className="relative group">
                {/* Outer glow */}
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-violet-500 opacity-80 blur-[2px] group-hover:opacity-100 transition-opacity" />
                <div className="relative rounded-2xl bg-gradient-to-br from-indigo-600 via-blue-600 to-violet-600 p-6 sm:p-8 shadow-2xl shadow-indigo-500/20">
                  {/* Badge */}
                  <div className="absolute -top-3 right-5 flex items-center gap-1.5 px-3 py-1 bg-white text-indigo-700 text-[11px] font-bold rounded-full shadow-lg shadow-indigo-500/15 ring-1 ring-indigo-100">
                    <Zap className="w-3 h-3" />
                    AI-Powered
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-indigo-300 mb-6">AI-Powered WebGenerator</p>
                  <div className="space-y-5">
                    {COMPARE_ROWS.map((row, i) => (
                      <div key={i} className="flex items-start gap-3.5">
                        <div className="mt-0.5 w-5 h-5 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 ring-1 ring-white/20">
                          <Check className="w-3 h-3 text-emerald-300" />
                        </div>
                        <span className={`text-sm leading-relaxed ${row.highlight ? "font-bold text-white" : "font-medium text-indigo-100"}`}>{row.us}</span>
                      </div>
                    ))}
                  </div>
                  {/* Subtle bottom highlight */}
                  <div className="mt-7 pt-5 border-t border-white/10">
                    <p className="text-xs text-indigo-200/80 leading-relaxed">KI erstellt Struktur, Texte & Design in Echtzeit.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FUNKTIONEN ===== */}
        <FeaturesSection navigate={navigate} />

        {/* ===== BEISPIELE ===== */}
        <ExampleSection />

        {/* ===== PREISE ===== */}
        <PricingSection navigate={navigate} />

        {/* ===== FAQ ===== */}
        <FaqSection />

        {/* ===== BOTTOM CTA ===== */}
        <section className="px-4 md:px-8 py-20 bg-slate-900" data-testid="bottom-cta-section">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Bereit? Deine Webseite wartet.
            </h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Bereits über 1.200 lokale Unternehmen nutzen WebGenerator.
            </p>
            <div className="relative group/btn inline-block">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-400 via-blue-400 to-violet-400 opacity-40 blur-lg group-hover/btn:opacity-70 transition-opacity" />
              <Button
                data-testid="cta-bottom-button"
                onClick={() => navigate("/assistent")}
                className="relative h-14 px-10 rounded-full text-lg font-medium bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500 hover:from-indigo-400 hover:via-blue-400 hover:to-violet-400 shadow-lg hover:-translate-y-0.5 transition-all"
              >
                Meine Webseite erstellen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="px-4 md:px-8 py-10 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Logo size="md" clickable={false} />
            </div>
            <a href="mailto:support@webgenerator.de" className="flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-600 transition-colors" data-testid="footer-email">
              <Mail className="w-4 h-4" />
              support@webgenerator.de
            </a>
          </div>
          <div className="border-t border-slate-100 mt-6 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; 2026 WebGenerator. Alle Rechte vorbehalten.</p>
            <div className="flex items-center gap-6">
              <button onClick={() => navigate("/impressum")} className="hover:text-indigo-600 transition-colors" data-testid="footer-impressum">Impressum</button>
              <button onClick={() => navigate("/datenschutz")} className="hover:text-indigo-600 transition-colors" data-testid="footer-datenschutz">Datenschutz</button>
              <button onClick={() => navigate("/agb")} className="hover:text-indigo-600 transition-colors" data-testid="footer-agb">AGB</button>
              <button onClick={() => navigate("/avv")} className="hover:text-indigo-600 transition-colors" data-testid="footer-avv">AVV</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
